import type { AssistantUiActionApiResource } from '@/api/assistant/assistantApi.types';

export interface VoiceCallApiResource {
  answer_sdp: string;
  max_session_seconds: number;
  minimum_average_log_probability: number;
  recording_confirmation_instructions: string;
}

export type VoiceHistoryRole = 'user' | 'assistant';

export interface VoiceHistoryEntryApiResource {
  conversation_id: string;
}

export interface VoiceCall {
  answerSdp: string;
  maxSessionSeconds: number;
  minimumAverageLogProbability: number;
  recordingConfirmationInstructions: string;
}

export interface SessionNoteDraftSettingApiResource {
  device_setting_field_id: string;
  value: string;
}

export interface SessionNoteDraftTreatmentApiResource {
  treatment_id: string;
  body_area_id: string | null;
  outcome: string;
  response: string | null;
  device_settings: SessionNoteDraftSettingApiResource[];
  corrects_earlier: boolean;
}

export interface SessionNoteDraftApiResource {
  pain_score: number | null;
  assessment: string | null;
  private_notes: string | null;
  body_area_ids: string[];
  treatments: SessionNoteDraftTreatmentApiResource[];
}

export interface SessionNoteUiActionApiResource {
  action_id: string;
  kind: 'session_note';
  operation: 'apply_draft' | 'save_draft';
  patient_id: string;
  draft?: SessionNoteDraftApiResource;
  needs_review?: string[];
}

export type VoiceUiActionApiResource =
  AssistantUiActionApiResource | SessionNoteUiActionApiResource;

export interface VoiceToolCallApiResource {
  output: string;
  ui_action: VoiceUiActionApiResource | null;
}

export interface VoiceToolCallResult {
  output: string;
  uiAction: VoiceUiActionApiResource | null;
}

export interface SessionNoteDraftSetting {
  deviceSettingFieldId: string;
  value: string;
}

export interface SessionNoteDraftTreatment {
  treatmentId: string;
  bodyAreaId: string | null;
  outcome: string;
  response: string | null;
  deviceSettings: SessionNoteDraftSetting[];
  correctsEarlier: boolean;
}

export interface SessionNoteDraft {
  painScore: number | null;
  assessment: string | null;
  privateNotes: string | null;
  bodyAreaIds: string[];
  treatments: SessionNoteDraftTreatment[];
}

export function isSessionNoteUiAction(
  action: VoiceUiActionApiResource
): action is SessionNoteUiActionApiResource {
  return action.kind === 'session_note';
}

export function mapApiResourceToSessionNoteDraft(
  resource: SessionNoteDraftApiResource
): SessionNoteDraft {
  return {
    painScore: resource.pain_score,
    assessment: resource.assessment,
    privateNotes: resource.private_notes,
    bodyAreaIds: resource.body_area_ids,
    treatments: resource.treatments.map(function mapTreatment(treatment) {
      return {
        treatmentId: treatment.treatment_id,
        bodyAreaId: treatment.body_area_id,
        outcome: treatment.outcome,
        response: treatment.response,
        deviceSettings: treatment.device_settings.map(function mapSetting(setting) {
          return { deviceSettingFieldId: setting.device_setting_field_id, value: setting.value };
        }),
        correctsEarlier: treatment.corrects_earlier === true,
      };
    }),
  };
}
